import { Button, Dropdown, Modal, SelectDropdownOption } from '@/components';
import { EntityTypes } from '@/constants';
import { queryClient } from '@/constants/query-client';
import { useUpdateRole } from '@/modules/Users/queries';
import { useGetBusinessAreaUserProfiles } from '@/modules/Users/queries/get-business-area-user-profiles';
import { UserKey } from '@/modules/Users/queries/types';
import { EditUserRoleJson } from '@/modules/Users/types';
import { getDropdownArray } from '@/utils/get-dropdown-array.util';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface EditBusinessAreaRoleModalProps {
  isOpen: boolean;
  handleClose: () => void;
  user: { id: number; role: number };
  businessAreaId: number;
}

export const EditBusinessAreaRoleModal = ({
  isOpen,
  handleClose,
  user,
  businessAreaId,
}: EditBusinessAreaRoleModalProps) => {
  const { t } = useTranslation();
  const { data } = useGetBusinessAreaUserProfiles();
  const { mutate } = useUpdateRole();

  const [dropdownRoleValue, setDropdownRoleValue] = useState<number>(user.role);

  const roleDropdownOptions = useMemo(
    () =>
      getDropdownArray(
        data?.map((role) => ({
          ...role,
          name: t(`roles.${role.name}`, { entity: 'business-area' }),
        })) ?? [],
      ),
    [data, t],
  );

  const onHandleConfirm = useCallback(
    (dropdownRoleValue: number) => {
      const convertedData: EditUserRoleJson = {
        id: user?.id,
        roleIds: [dropdownRoleValue],
        entityType: EntityTypes.BUSINESS_AREA,
        entityId: businessAreaId,
      };

      mutate(convertedData, {
        onError: () => {
          toast.error(t('error_updating_user'));
        },
        onSuccess: () => {
          queryClient.invalidateQueries(UserKey.ALL);
          toast.success(t('success_updating_user'));
          handleClose();
        },
      });
    },
    [mutate, t, handleClose, user, businessAreaId],
  );

  const handleConfirm = () => {
    dropdownRoleValue && onHandleConfirm(dropdownRoleValue);
  };

  const handleOnSelectRoleDropdownValue = useCallback(
    (option: SelectDropdownOption<number>) =>
      setDropdownRoleValue(option.value),
    [],
  );

  return (
    <Modal
      isOpen={isOpen}
      title={t('edit_user')}
      className="h-fit w-128"
      handleClose={handleClose}
    >
      <div className="mt-6 flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <div className="mt-2 flex min-h-72 flex-col overflow-y-auto p-2">
            <Dropdown
              className="h-10 w-full rounded-md  bg-brightGray capitalize italic"
              placeholder={t('role')}
              options={roleDropdownOptions}
              value={dropdownRoleValue}
              onSelect={handleOnSelectRoleDropdownValue}
            />
          </div>
          <div className="mt-8 flex justify-between">
            <Button
              onClick={handleClose}
              variant="outline"
              className="h-9 w-24"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              className="h-9 w-24"
              isDisabled={!dropdownRoleValue}
            >
              {t('confirm')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
